import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { useImportJobsStore } from '../../../application/store/importJobsStore';
import { ActiveImportJob, ImportJobStatus } from '@domain/connection/models/ImportJob';

const POLL_INTERVAL_MS = 5000;

export interface TrackedImportJob {
  id: string;
  connector: string;
  label: string;
  startedAt: number;
  status?: ImportJobStatus;
  isLoading: boolean;
}

/**
 * Polls every job tracked in the global import-jobs store, regardless of which connector started it.
 *
 * Active/waiting/delayed jobs are resolved from a single bulk `fetchActiveJobs` poll instead of one
 * request per job. A job that drops out of that list has finished, so its final state/result is fetched
 * once via `fetchJobStatus` rather than being polled.
 */
export const useImportJobs = () => {
  const jobs = useImportJobsStore((s) => s.jobs);
  const removeJob = useImportJobsStore((s) => s.removeJob);

  const activeJobsQuery = useQuery({
    queryKey: ['active-import-jobs'],
    queryFn: async () => {
      const response = await container.importJobsRepository.fetchActiveJobs();
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    },
    enabled: jobs.length > 0,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const activeById = useMemo(() => {
    const map = new Map<string, ActiveImportJob>();
    activeJobsQuery.data?.forEach((job) => map.set(job.jobId, job));
    return map;
  }, [activeJobsQuery.data]);

  const finishedJobs = jobs.filter((job) => activeJobsQuery.data !== undefined && !activeById.has(job.id));

  const finalStatusQueries = useQueries({
    queries: finishedJobs.map((job) => ({
      queryKey: ['import-job-final-status', job.connector, job.id],
      queryFn: async () => {
        const response = await container.importJobsRepository.fetchJobStatus(job.connector, job.id);
        if (response.status === 'error') throw new Error(response.error.message);
        return response.data;
      },
      staleTime: Infinity,
      refetchInterval: false as const,
    })),
  });

  const finalStatusById = useMemo(() => {
    const map = new Map<string, ImportJobStatus>();
    finishedJobs.forEach((job, index) => {
      const data = finalStatusQueries[index]?.data;
      if (data) map.set(job.id, data);
    });
    return map;
  }, [finishedJobs, finalStatusQueries]);

  const trackedJobs: TrackedImportJob[] = jobs.map((job) => {
    const active = activeById.get(job.id);
    const status: ImportJobStatus | undefined = active
      ? { state: active.state, progress: active.progress, result: active.result }
      : finalStatusById.get(job.id);

    return {
      ...job,
      status,
      isLoading: activeJobsQuery.isLoading || (!active && !status && activeJobsQuery.data !== undefined),
    };
  });

  return { jobs: trackedJobs, removeJob };
};
